import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Devices,
  CheckCircle,
  Error,
  Schedule,
  Info,
  Monitor,
} from '@mui/icons-material';
import DeviceScreenMonitor from '../components/DeviceScreenMonitor';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { useTranslation } from 'react-i18next';

interface Device {
  id: string;
  name: string;
  ip: string;
  status: 'online' | 'offline';
  apks: Array<{
    id: string;
    name: string;
    paymentSystem: string;
    currentEnvironment: string;
    isActive: boolean;
  }>;
}

interface TestConfig {
  deviceId: string;
  apkId: string;
  environment: string;
  testType: 'full-e2e' | 'payment-only' | 'login-only' | 'custom';
  integration: 'square' | 'toast' | 'lightspeed' | 'non-integration' | 'clover';
  customSteps?: string[];
}

interface TestResult {
  id: string;
  status: 'running' | 'completed' | 'failed';
  progress: number;
  currentStep: string;
  startTime: string;
  endTime?: string;
  duration?: number;
  error?: string;
  logs: Array<{
    timestamp: string;
    level: 'info' | 'warn' | 'error';
    message: string;
  }>;
  screenshots: string[];
}

const TestRunner: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [config, setConfig] = useState<TestConfig>({
    deviceId: '',
    apkId: '',
    environment: 'staging',
    testType: 'full-e2e',
    integration: 'square',
  });
  const [selectedEnvironment, setSelectedEnvironment] = useState(0);
  const [apkData, setApkData] = useState<any>({});
  
  const environments = ['staging', 'production', 'beta', 'production-new'];

  const [currentTest, setCurrentTest] = useState<TestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);

  // 清理 SSE 连接
  const cleanupEventSource = () => {
    if (eventSource) {
      console.log('清理SSE连接');
      eventSource.close();
      setEventSource(null);
    }
  };

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      cleanupEventSource();
    };
  }, []);

  // 加载APK数据 - 使用新的API端点
  useEffect(() => {
    const loadAPKData = async () => {
      try {
        const response = await fetch(`${API_ENDPOINTS.TESTS_NEW}/environments?t=${Date.now()}`);
        if (response.ok) {
          const data = await response.json();
          console.log('TestRunner: Loaded APK data:', data);
          setApkData(data);
        } else {
          console.error('Failed to load APK data:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('Failed to load APK data:', error);
      }
    };
    loadAPKData();
  }, []);

  // 获取设备列表
  const { data: devices, isLoading: devicesLoading, refetch: refetchDevices } = useQuery<Device[]>({
    queryKey: ['devices'],
    queryFn: () => axios.get(API_ENDPOINTS.DEVICES).then(res => res.data),
    refetchInterval: 10000, // 每10秒自动刷新设备状态
  });

  // 获取APK版本数据
  const { data: apkVersions, isLoading: apkVersionsLoading } = useQuery({
    queryKey: ['apk-versions'],
    queryFn: () => axios.get(API_ENDPOINTS.APK_VERSIONS).then(res => res.data),
  });

  // 更新所有设备状态
  const updateDeviceStatusMutation = useMutation({
    mutationFn: () => axios.post(`${API_ENDPOINTS.DEVICES}/update-status`),
    onSuccess: () => {
      enqueueSnackbar('设备状态已更新', { variant: 'success' });
      refetchDevices();
    },
    onError: (error: any) => {
      enqueueSnackbar(`更新设备状态失败: ${error.message}`, { variant: 'error' });
    },
  });

  // 获取当前运行的测试
  const { data: runningTests } = useQuery<TestResult[]>({
    queryKey: ['running-tests'],
    queryFn: () => axios.get(`${API_ENDPOINTS.TESTS}/running`).then(res => res.data),
    refetchInterval: 2000,
  });

  // 同步当前测试状态
  React.useEffect(() => {
    if (runningTests && runningTests.length > 0) {
      const latestTest = runningTests[0];
      if (!currentTest || currentTest.id !== latestTest.id) {
        setCurrentTest(latestTest);
      }
    } else {
      // 🎯 修复：如果没有运行中的测试，立即清空 currentTest（除非正在重试）
      if (currentTest && !isRetrying) {
        // 如果测试状态是 running，说明测试已结束
        if (currentTest.status === 'running') {
          setCurrentTest(null);
        } else if (currentTest.status === 'completed' || currentTest.status === 'failed') {
          // 如果测试已完成或失败，延迟清空（让用户看到结果）
          const timer = setTimeout(() => {
            setCurrentTest(null);
          }, 3000); // 3秒后清空
          
          return () => clearTimeout(timer);
        } else {
          // 其他状态立即清空
          setCurrentTest(null);
        }
      }
    }
  }, [runningTests, currentTest, isRetrying]);

  // 开始测试
  const startTestMutation = useMutation({
    mutationFn: (testConfig: TestConfig) =>
      axios.post(`${API_ENDPOINTS.TESTS}/start`, testConfig),
    onSuccess: (response) => {
      const testId = response.data.testId;
      setTestError(null);
      setIsRetrying(false);
      enqueueSnackbar('测试已开始执行', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['running-tests'] });

      // 监听测试状态
      listenToTestUpdates(testId);
    },
    onError: (error: any) => {
      const errorResponse = error.response?.data;
      const errorMessage = errorResponse?.error || error.message || '启动测试失败';
      setTestError(errorMessage);
      enqueueSnackbar(`启动测试失败: ${errorMessage}`, { variant: 'error' });
      setIsRetrying(false);
      // 🎯 修复：启动失败时清空 currentTest，允许重新尝试
      setCurrentTest(null);
    },
  });

  // 停止测试
  const stopTestMutation = useMutation({
    mutationFn: (testId: string) =>
      axios.post(`${API_ENDPOINTS.TESTS}/${testId}/stop`),
    onSuccess: () => {
      enqueueSnackbar('测试已停止', { variant: 'info' });
      setCurrentTest(null);
      queryClient.invalidateQueries({ queryKey: ['running-tests'] });
    },
    onError: () => {
      enqueueSnackbar('停止测试失败', { variant: 'error' });
    },
  });

  // 清理所有运行中的测试
  const cleanupMutation = useMutation({
    mutationFn: () => axios.post(`${API_ENDPOINTS.TESTS}/cleanup`),
    onSuccess: () => {
      // 关闭旧的 SSE 连接
      cleanupEventSource();
      
      enqueueSnackbar('所有运行中的测试已清理', { variant: 'success' });
      setTestError(null);
      setCurrentTest(null);
      queryClient.invalidateQueries({ queryKey: ['running-tests'] });
    },
    onError: (error: any) => {
      enqueueSnackbar(`清理失败: ${error.message}`, { variant: 'error' });
    },
  });

  // 监听测试状态更新
  const listenToTestUpdates = (testId: string) => {
    console.log('开始监听测试更新:', testId);
    
    // 关闭旧的连接
    cleanupEventSource();
    
    const newEventSource = new EventSource(`${API_ENDPOINTS.TESTS}/${testId}/events`);
    setEventSource(newEventSource);
    let timeoutId: NodeJS.Timeout;
    
    // 设置超时处理（20分钟）
    timeoutId = setTimeout(() => {
      console.log('测试执行超时');
      newEventSource.close();
      setEventSource(null);
      setTestError('测试执行超时，请检查设备连接状态');
      enqueueSnackbar('测试执行超时', { variant: 'error' });
      setCurrentTest(prev => prev ? { ...prev, status: 'failed', error: '测试执行超时' } : null);
    }, 20 * 60 * 1000);

    newEventSource.onopen = () => {
      console.log('SSE连接已建立');
    };

    newEventSource.onmessage = (event) => {
      try {
        const update = JSON.parse(event.data);
        console.log('收到测试状态更新:', update);
        
        // 合并更新到当前测试状态，保留完整的日志历史
        setCurrentTest(prev => {
          if (!prev) return update;
          
          return {
            ...prev,
            ...update,
            logs: update.logs || prev.logs, // 使用新的日志或保持原有日志
            startTime: prev.startTime, // 保持开始时间
            endTime: update.endTime || prev.endTime, // 更新结束时间
            duration: update.duration || prev.duration, // 更新持续时间
            screenshots: update.screenshots || prev.screenshots, // 保持截图列表
            error: update.error || prev.error // 更新错误信息
          };
        });

        if (update.status === 'completed') {
          clearTimeout(timeoutId);
          setTestError(null);
          enqueueSnackbar('测试执行完成', { variant: 'success' });
          newEventSource.close();
          setEventSource(null);
        } else if (update.status === 'failed') {
          clearTimeout(timeoutId);
          setTestError(update.error || t('testRunner.messages.testFailed'));
          enqueueSnackbar(t('testRunner.messages.testFailed'), { variant: 'error' });
          newEventSource.close();
          setEventSource(null);
        }
      } catch (error) {
        console.error('解析测试状态更新失败:', error);
      }
    };

    newEventSource.onerror = (error) => {
      console.error('SSE连接错误:', error);
      console.error('EventSource readyState:', newEventSource.readyState);
      clearTimeout(timeoutId);
      newEventSource.close();
      setEventSource(null);
      setTestError(t('testRunner.connectionInterrupted'));
      enqueueSnackbar(t('testRunner.connectionInterrupted'), { variant: 'error' });
      setCurrentTest(prev => prev ? { ...prev, status: 'failed', error: t('testRunner.connectionInterrupted') } : null);
    };
  };

  // 处理配置变化
  const handleConfigChange = (field: keyof TestConfig, value: any) => {
    setConfig(prev => {
      const newConfig = { ...prev, [field]: value };
      
      // 如果设备改变，清空APK选择
      if (field === 'deviceId') {
        newConfig.apkId = '';
      }
      
      return newConfig;
    });
  };

  // 处理环境切换
  const handleEnvironmentChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedEnvironment(newValue);
    const environment = environments[newValue];
    setConfig(prev => ({ ...prev, environment }));
  };

  // 获取选中设备的APK列表
  const currentDevice = devices?.find(d => d.id === config.deviceId);
  
  // 根据测试环境获取当前环境的APK信息 - 使用新的API数据结构
  const currentEnvironmentApk = React.useMemo(() => {
    if (!apkData || !Array.isArray(apkData) || apkData.length === 0) return null;
    
    const environment = environments[selectedEnvironment];
    console.log('TestRunner: Looking for environment:', environment);
    console.log('TestRunner: Available APKs:', apkData);
    
    // 查找匹配环境的APK
    const matchingApk = apkData.find(apk => apk.environment === environment);
    
    if (!matchingApk) {
      console.log('TestRunner: No APK found for environment:', environment);
      return null;
    }
    
    console.log('TestRunner: Found matching APK:', matchingApk);
    
    return {
      id: matchingApk.id,
      name: matchingApk.name,
      paymentSystem: 'square', // 默认支付系统
      packageName: matchingApk.packageName || 'com.infi.sos2',
      environment: matchingApk.environment,
      version: matchingApk.currentVersion,
      isActive: matchingApk.currentVersion.isActive || false
    };
  }, [apkData, selectedEnvironment]);

  // 当环境APK变化时，自动设置APK ID
  React.useEffect(() => {
    if (currentEnvironmentApk && currentEnvironmentApk.id !== config.apkId) {
      setConfig(prev => ({ ...prev, apkId: currentEnvironmentApk.id }));
    }
  }, [currentEnvironmentApk, config.apkId]);
  
  // 调试信息
  console.log('Selected device:', currentDevice);
  console.log('Current environment APK:', currentEnvironmentApk);
  console.log('Config deviceId:', config.deviceId);
  console.log('Config environment:', config.environment);
  console.log('APK Versions data:', apkVersions);

  // 开始测试
  const handleStartTest = () => {
    if (!config.deviceId || !config.apkId) {
      enqueueSnackbar('请选择设备和APK', { variant: 'warning' });
      return;
    }

    setTestError(null);
    startTestMutation.mutate(config);
  };

  // 重试测试
  const handleRetryTest = () => {
    if (!config.deviceId || !config.apkId) {
      enqueueSnackbar('请选择设备和APK', { variant: 'warning' });
      return;
    }

    setIsRetrying(true);
    setTestError(null);
    startTestMutation.mutate(config);
  };

  // 关闭屏幕监控
  const handleCloseScreenMonitor = () => {
    // 在嵌入式模式下，这个函数可以用于清理资源
  };

  // 清除错误
  const handleClearError = () => {
    setTestError(null);
    setIsRetrying(false);
  };

  // 清理所有运行中的测试
  const handleCleanupAll = () => {
    cleanupMutation.mutate();
  };

  // 停止测试
  const handleStopTest = () => {
    if (currentTest) {
      stopTestMutation.mutate(currentTest.id);
    }
  };

  // 清理测试状态
  const handleClearTest = () => {
    setCurrentTest(null);
    setTestError(null);
  };

  // 获取状态颜色
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'primary';
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  // 获取状态图标
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running': return <Schedule color="primary" />;
      case 'completed': return <CheckCircle color="success" />;
      case 'failed': return <Error color="error" />;
      default: return <Info />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('testRunner.title')}
      </Typography>

      <Grid container spacing={3}>
        {/* 测试配置 */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ minHeight: '600px' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('testRunner.testConfig')}
              </Typography>
              
              {/* 调试信息 */}
              {process.env.NODE_ENV === 'development' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                     {t('testRunner.debugInfo')}: {t('testRunner.deviceCount')}: {devices?.length || 0}, 
                     {t('testRunner.selectedDevice')}: {currentDevice?.name || t('testRunner.none')}, 
                     {t('testRunner.availableAPK')}: {currentEnvironmentApk ? 1 : 0}, 
                     Environment: {config.environment}
                  </Typography>
                </Alert>
              )}

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <FormControl fullWidth size="medium">
                      <InputLabel id="device-select-label">{t('testRunner.selectDevice')}</InputLabel>
                    <Select
                      labelId="device-select-label"
                      value={config.deviceId}
                      onChange={(e) => handleConfigChange('deviceId', e.target.value)}
                      disabled={devicesLoading}
                      label={t('testRunner.selectDevice')}
                    >
                      {devices?.map((device) => (
                        <MenuItem key={device.id} value={device.id}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Devices />
                            {device.name} ({device.ip})
                            <Chip
                              label={device.status}
                              color={device.status === 'online' ? 'success' : 'error'}
                              size="small"
                            />
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => updateDeviceStatusMutation.mutate()}
                      disabled={updateDeviceStatusMutation.isPending}
                      sx={{ minWidth: 'auto', px: 2 }}
                    >
                      {updateDeviceStatusMutation.isPending ? t('testRunner.updatingStatus') : t('testRunner.refreshStatus')}
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {t('testRunner.selectAPK')}
                  </Typography>
                  
                  <Tabs 
                    value={selectedEnvironment} 
                    onChange={handleEnvironmentChange} 
                    sx={{ mb: 2 }}
                    variant="fullWidth"
                  >
                    {environments.map((env) => (
                      <Tab key={env} label={env.toUpperCase().replace('-', ' ')} />
                    ))}
                  </Tabs>

                  {currentEnvironmentApk ? (
                    <Card 
                      sx={{ 
                        border: 2,
                        borderColor: 'primary.main',
                        bgcolor: 'primary.50',
                        mb: 2
                      }}
                    >
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                          <Typography variant="h6" component="h3">
                            {currentEnvironmentApk.environment.charAt(0).toUpperCase() + currentEnvironmentApk.environment.slice(1)} APK
                          </Typography>
                          <Chip 
                            label={currentEnvironmentApk.environment.toUpperCase()} 
                            color="primary"
                            size="small" 
                          />
                        </Box>

                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Package: {currentEnvironmentApk.packageName}
                        </Typography>

                        <Box mb={2}>
                          <Typography variant="subtitle2" gutterBottom>
                            {t('testRunner.currentVersion')} ({currentEnvironmentApk.environment}):
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h6" color="primary">
                              v{currentEnvironmentApk.version.version}
                            </Typography>
                            {currentEnvironmentApk.isActive && (
                              <Chip icon={<CheckCircle />} label="Active" color="success" size="small" />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Build: {currentEnvironmentApk.version.buildNumber} | {currentEnvironmentApk.version.releaseDate}
                          </Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {currentEnvironmentApk.environment.charAt(0).toUpperCase() + currentEnvironmentApk.environment.slice(1)}环境APK,包含所有集成功能
                        </Typography>

                        <Box display="flex" alignItems="center" gap={1} color="primary.main">
                          <CheckCircle fontSize="small" />
                          <Typography variant="body2" fontWeight="bold">
                            {t('testRunner.selected')}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t('testRunner.messages.noAPKAvailable')}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth size="medium" sx={{ mb: 1 }}>
                    <InputLabel id="testtype-select-label">{t('testRunner.testType')}</InputLabel>
                    <Select
                      labelId="testtype-select-label"
                      value={config.testType}
                      onChange={(e) => handleConfigChange('testType', e.target.value)}
                      label={t('testRunner.testType')}
                    >
                      <MenuItem value="full-e2e">{t('testRunner.testTypes.fullE2E')}</MenuItem>
                      <MenuItem value="payment-only">{t('testRunner.testTypes.paymentOnly')}</MenuItem>
                      <MenuItem value="login-only">{t('testRunner.testTypes.loginOnly')}</MenuItem>
                      <MenuItem value="custom">{t('testRunner.testTypes.custom')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth size="medium" sx={{ mb: 1 }}>
                    <InputLabel id="integration-select-label">{t('testRunner.integrationSelection')}</InputLabel>
                    <Select
                      labelId="integration-select-label"
                      value={config.integration}
                      onChange={(e) => handleConfigChange('integration', e.target.value)}
                      label={t('testRunner.integrationSelection')}
                    >
                      <MenuItem value="square">
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box sx={{ width: 20, height: 20, bgcolor: '#00C851', borderRadius: 1 }} />
                          {t('testRunner.integrations.square')}
                        </Box>
                      </MenuItem>
                      <MenuItem value="toast">
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box sx={{ width: 20, height: 20, bgcolor: '#FF6B35', borderRadius: 1 }} />
                          {t('testRunner.integrations.toast')}
                        </Box>
                      </MenuItem>
                      <MenuItem value="lightspeed">
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box sx={{ width: 20, height: 20, bgcolor: '#0070F3', borderRadius: 1 }} />
                          {t('testRunner.integrations.lightspeed')}
                        </Box>
                      </MenuItem>
                      <MenuItem value="clover">
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box sx={{ width: 20, height: 20, bgcolor: '#00D4AA', borderRadius: 1 }} />
                          {t('testRunner.integrations.clover')}
                        </Box>
                      </MenuItem>
                      <MenuItem value="non-integration">
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box sx={{ width: 20, height: 20, bgcolor: '#6C757D', borderRadius: 1 }} />
                          {t('testRunner.integrations.nonIntegration')}
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" gap={2} flexDirection="column" sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<PlayArrow />}
                      onClick={handleStartTest}
                      disabled={startTestMutation.isLoading || !!(currentTest && currentTest.status === 'running')}
                      fullWidth
                      sx={{ py: 1.5, fontSize: '1.1rem' }}
                    >
                      {startTestMutation.isLoading ? t('testRunner.starting') : t('testRunner.startTest')}
                    </Button>

                    {currentTest && (
                      <Button
                        variant="outlined"
                        size="large"
                        startIcon={<Stop />}
                        onClick={handleStopTest}
                        disabled={stopTestMutation.isLoading}
                        color="error"
                        fullWidth
                      >
                        {t('testRunner.stopTest')}
                      </Button>
                    )}

                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<Error />}
                      onClick={handleCleanupAll}
                      disabled={cleanupMutation.isLoading}
                      fullWidth
                      color="warning"
                      sx={{ py: 1.5, fontSize: '1rem' }}
                    >
                      {cleanupMutation.isLoading ? t('testRunner.cleaning') : t('testRunner.cleanupTests')}
                    </Button>
                  </Box>
                </Grid>

                 {/* 错误显示和重试按钮 */}
                {testError && (
                  <Grid item xs={12}>
                    <Alert 
                      severity="error" 
                      action={
                        <Box display="flex" gap={1}>
                          <Button
                            color="inherit"
                            size="small"
                            onClick={handleRetryTest}
                            disabled={isRetrying || startTestMutation.isLoading}
                          >
                             {isRetrying ? t('testRunner.retrying') : t('testRunner.retryTest')}
                          </Button>
                          <Button
                            color="inherit"
                            size="small"
                            onClick={handleClearError}
                          >
                             {t('common.reset')}
                          </Button>
                        </Box>
                      }
                    >
                      <Typography variant="body2">
                        <strong>{t('testRunner.testError')}:</strong> {testError}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* 测试状态 */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                 {t('testRunner.testStatus')}
              </Typography>

              {currentTest ? (
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={currentTest.status}
                        color={getStatusColor(currentTest.status)}
                        icon={getStatusIcon(currentTest.status)}
                      />
                      <Typography variant="body2">
                        {currentTest.currentStep}
                      </Typography>
                    </Box>
                    {currentTest.status === 'completed' && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleClearTest}
                        color="secondary"
                      >
                        清理状态
                      </Button>
                    )}
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={currentTest.progress}
                    sx={{ height: 10, borderRadius: 5, mb: 2 }}
                  />

                  <Typography variant="body2" color="textSecondary" gutterBottom>
                     {t('testRunner.progress')}: {currentTest.progress}%
                  </Typography>

                  <Typography variant="body2" color="textSecondary">
                    {t('testRunner.startTime')}: {new Date(currentTest.startTime).toLocaleString()}
                  </Typography>

                  {currentTest.endTime && (
                    <Typography variant="body2" color="textSecondary">
                      {t('testRunner.endTime')}: {new Date(currentTest.endTime).toLocaleString()}
                    </Typography>
                  )}

                  {currentTest.error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                         <strong>{t('testRunner.executionError')}:</strong> {currentTest.error}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              ) : testError ? (
                <Box textAlign="center" py={4}>
                  <Error color="error" sx={{ fontSize: 48, mb: 2 }} />
                  <Typography variant="body1" color="error" gutterBottom>
                     {t('testRunner.messages.testFailed')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {testError}
                  </Typography>
                  <Box display="flex" gap={2} justifyContent="center" mt={2}>
                    <Button
                      variant="contained"
                      onClick={handleRetryTest}
                      disabled={isRetrying || startTestMutation.isLoading}
                    >
                       {isRetrying ? t('testRunner.retrying') : t('testRunner.retryTest')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleClearError}
                    >
                       {t('common.reset')}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Alert severity="info">
                   {t('testRunner.messages.noRunningTests')}
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* 运行中的测试列表 */}
          {runningTests && runningTests.length > 0 && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🔄 所有运行中的测试
                </Typography>

                <List>
                  {runningTests.map((test) => (
                    <ListItem key={test.id}>
                      <ListItemIcon>
                        {getStatusIcon(test.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={`测试 ${test.id}`}
                        secondary={`${test.currentStep} (${test.progress}%)`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

          {/* 屏幕监控区域 */}
          <Card sx={{ mt: 2, height: '1200px', minHeight: '1200px' }}>
            <CardContent sx={{ height: '100%', p: 0, display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  overflow: 'hidden',
                  minHeight: '1100px'
                }}
              >
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'grey.200', flexShrink: 0 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" gutterBottom>
                         {t('testRunner.deviceScreenMonitor')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {currentDevice ? `${currentDevice.name} (${currentDevice.ip})` : t('testRunner.messages.deviceNotSelected')}
                      </Typography>
                    </Box>
                    {currentDevice && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const monitorUrl = `${window.location.origin}/#/device-monitor?device=${currentDevice.id}`;
                          window.open(monitorUrl, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
                        }}
                        startIcon={<Monitor />}
                      >
                        {t('deviceScreenMonitor.openIndependentMonitor')}
                      </Button>
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ flex: 1, p: 1 }}>
                  {currentDevice ? (
                    <DeviceScreenMonitor
                      open={true}
                      onClose={handleCloseScreenMonitor}
                      deviceId={currentDevice.id}
                      deviceIp={currentDevice.ip}
                      deviceName={currentDevice.name}
                      embedded={true}
                    />
                  ) : (
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center" 
                      height="100%"
                      color="text.secondary"
                      flexDirection="column"
                      gap={2}
                    >
                      <Typography variant="h6">
                         📱 {t('testRunner.selectDeviceToMonitor')}
                      </Typography>
                      <Typography variant="body2">
                        {t('testRunner.selectDeviceFromLeft')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 测试日志 */}
      {currentTest && currentTest.logs.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📝 测试日志
            </Typography>

            <Paper sx={{ maxHeight: 400, overflow: 'auto', p: 2 }}>
              {currentTest.logs.map((log, index) => (
                <Box key={index} mb={1}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: log.level === 'error' ? 'error.main' :
                             log.level === 'warn' ? 'warning.main' : 'text.primary',
                      fontFamily: 'monospace',
                    }}
                  >
                    [{new Date(log.timestamp).toLocaleTimeString()}] {log.message}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </CardContent>
        </Card>
      )}

    </Box>
  );
};

export default TestRunner;
